/**
 * Client-side identity check — registers the candidate face at session start
 * and flags sustained mismatches (person swap).
 */
const HIST_BINS = 32;
const PATCH_GRID = 4;
const REGISTER_SAMPLES = 3;
const MISMATCH_STREAK_THRESHOLD = 3;
const MATCH_THRESHOLD = 0.68;

function cropFaceRegion(videoEl) {
  if (!videoEl || videoEl.readyState < 2 || !videoEl.videoWidth) return null;

  const w = videoEl.videoWidth;
  const h = videoEl.videoHeight;
  const cropW = w * 0.62;
  const cropH = h * 0.72;
  const sx = (w - cropW) / 2;
  const sy = h * 0.08;

  const canvas = document.createElement('canvas');
  canvas.width = 64;
  canvas.height = 64;
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  ctx.drawImage(videoEl, sx, sy, cropW, cropH, 0, 0, 64, 64);

  const { data } = ctx.getImageData(0, 0, 64, 64);
  const hist = new Float32Array(HIST_BINS);
  const patchSize = 64 / PATCH_GRID;
  const patchMeans = new Float32Array(PATCH_GRID * PATCH_GRID);

  for (let py = 0; py < PATCH_GRID; py += 1) {
    for (let px = 0; px < PATCH_GRID; px += 1) {
      let sum = 0;
      let count = 0;
      const startY = py * patchSize;
      const startX = px * patchSize;
      for (let y = startY; y < startY + patchSize; y += 1) {
        for (let x = startX; x < startX + patchSize; x += 1) {
          const idx = (y * 64 + x) * 4;
          const lum = 0.299 * data[idx] + 0.587 * data[idx + 1] + 0.114 * data[idx + 2];
          sum += lum;
          count += 1;
          const bin = Math.min(HIST_BINS - 1, Math.floor(lum / (256 / HIST_BINS)));
          hist[bin] += 1;
        }
      }
      patchMeans[py * PATCH_GRID + px] = sum / count;
    }
  }

  const histSum = hist.reduce((acc, v) => acc + v, 0) || 1;
  for (let i = 0; i < HIST_BINS; i += 1) hist[i] /= histSum;

  let patchMean = 0;
  for (let i = 0; i < patchMeans.length; i += 1) patchMean += patchMeans[i];
  patchMean /= patchMeans.length;
  let patchVar = 0;
  for (let i = 0; i < patchMeans.length; i += 1) {
    const d = patchMeans[i] - patchMean;
    patchVar += d * d;
  }
  const patchStd = Math.sqrt(patchVar / patchMeans.length) + 1e-8;
  for (let i = 0; i < patchMeans.length; i += 1) {
    patchMeans[i] = (patchMeans[i] - patchMean) / patchStd;
  }

  return { hist, patches: patchMeans };
}

function vectorSimilarity(a, b) {
  let dot = 0;
  let n1 = 0;
  let n2 = 0;
  for (let i = 0; i < a.length; i += 1) {
    dot += a[i] * b[i];
    n1 += a[i] * a[i];
    n2 += b[i] * b[i];
  }
  return dot / (Math.sqrt(n1) * Math.sqrt(n2) + 1e-8);
}

function embeddingSimilarity(a, b) {
  const histSim = vectorSimilarity(a.hist, b.hist);
  const patchSim = vectorSimilarity(a.patches, b.patches);
  return 0.35 * histSim + 0.65 * patchSim;
}

function averageEmbeddings(samples) {
  const avgHist = new Float32Array(HIST_BINS);
  const avgPatches = new Float32Array(PATCH_GRID * PATCH_GRID);
  for (const sample of samples) {
    for (let i = 0; i < HIST_BINS; i += 1) avgHist[i] += sample.hist[i];
    for (let i = 0; i < avgPatches.length; i += 1) avgPatches[i] += sample.patches[i];
  }
  for (let i = 0; i < HIST_BINS; i += 1) avgHist[i] /= samples.length;
  for (let i = 0; i < avgPatches.length; i += 1) avgPatches[i] /= samples.length;
  return { hist: avgHist, patches: avgPatches };
}

export class LocalFaceIdentity {
  constructor() {
    this.baseline = null;
    this.samples = [];
    this.mismatchStreak = 0;
    this.registered = false;
    this.mismatchLocked = false;
  }

  reset() {
    this.baseline = null;
    this.samples = [];
    this.mismatchStreak = 0;
    this.registered = false;
    this.mismatchLocked = false;
  }

  /**
   * @returns {{ registered: boolean, match: boolean|null, similarity: number|null }}
   */
  evaluate(videoEl) {
    const embedding = cropFaceRegion(videoEl);
    if (!embedding) return { registered: false, match: null, similarity: null };

    if (!this.registered) {
      this.samples.push(embedding);
      if (this.samples.length >= REGISTER_SAMPLES) {
        this.baseline = averageEmbeddings(this.samples);
        this.registered = true;
      }
      return { registered: this.registered, match: null, similarity: null };
    }

    if (this.mismatchLocked) {
      const similarity = embeddingSimilarity(this.baseline, embedding);
      return { registered: true, match: false, similarity };
    }

    const similarity = embeddingSimilarity(this.baseline, embedding);
    if (similarity < MATCH_THRESHOLD) {
      this.mismatchStreak += 1;
    } else {
      this.mismatchStreak = 0;
    }

    if (this.mismatchStreak >= MISMATCH_STREAK_THRESHOLD) {
      this.mismatchLocked = true;
      return { registered: true, match: false, similarity };
    }

    if (this.mismatchStreak > 0) {
      return { registered: true, match: null, similarity };
    }

    return { registered: true, match: true, similarity };
  }
}

export const IDENTITY_MISMATCH_MSG =
  'The candidate is different. The person in the camera does not match the person who started the exam.';
